<template>
  <m-alert
    :type="type"
    :icon="icon"
    :size="size"
    :title="title"
    :description="description"
    :dismissible="false"
    :toast="true"
    :links-text="linksText"
    :links-href="linksHref"
    :links-target="linksTarget"
    :class="['v-toast__item', `v-toast__item--${type}`, `v-toast__item--${position}`, 'show']"
    :prefix-class="prefixClass"
    @mouseover="toggleTimer(true)"
    @mouseleave="toggleTimer(false)"
  />
</template>

<script>
import eventBus from './bus';
import Positions from './positions';
import Timer from './timer';
import { removeElement } from './helpers';
import MAlert from '../Alert';

export default {
  name: 'ToastContainer',
  components: {
    MAlert,
  },
  props: {
    type: {
      type: String,
      default: 'success',
    },
    icon: {
      type: String,
      default: null,
    },
    size: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    description: {
      type: String,
      required: true,
    },
    wrapperClass: {
      type: String,
      default: '',
    },
    prefixClass: {
      type: String,
      default: '',
    },
    plugin: {
      type: Boolean,
      default: true,
    },
    position: {
      type: String,
      default: Positions.TOP,
      validator(value) {
        return Object.values(Positions).includes(value);
      },
    },
    linksText: {
      type: Array,
      default: () => [],
    },
    linksHref: {
      type: Array,
      default: () => [],
    },
    linksTarget: {
      type: Array,
      default: () => [],
    },
    duration: {
      type: Number,
      default: 500000000,
    },
    onDismiss: {
      type: Function,
      default: () => {},
    },
    onClick: {
      type: Function,
      default: () => {},
    },
    queue: Boolean,
    pauseOnHover: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    correctParent() {
      switch (this.position) {
        case Positions.TOP:
        case Positions.TOP_RIGHT:
        case Positions.TOP_LEFT:
        default:
          return this.parentTop;
        case Positions.BOTTOM:
        case Positions.BOTTOM_RIGHT:
        case Positions.BOTTOM_LEFT:
          return this.parentBottom;
      }
    },
  },
  beforeMount() {
    this.setupContainer();
  },
  mounted() {
    this.showNotice();
    eventBus.on('toast-clear', this.dismiss);
  },
  beforeDestroy() {
    eventBus.off('toast-clear', this.dismiss);
  },
  methods: {
    setupContainer() {
      this.parentTop = document.querySelector('.v-toast.v-toast--top');
      this.parentBottom = document.querySelector('.v-toast.v-toast--bottom');
      // No need to create them, they already exists
      if (this.parentTop && this.parentBottom) return;
      if (!this.parentTop) {
        this.parentTop = document.createElement('div');
        this.parentTop.className = `v-toast v-toast--top`;
      }
      if (!this.parentBottom) {
        this.parentBottom = document.createElement('div');
        this.parentBottom.className = `v-toast v-toast--bottom`;
      }

      const container = document.body;

      if (this.wrapperClass !== '') {
        let wrapper = document.querySelector('.v-toast__wrapper');
        if (!wrapper) {
          wrapper = document.createElement('div');
          wrapper.className = `v-toast__wrapper ${this.wrapperClass}`;
        }

        wrapper.appendChild(this.parentTop);
        wrapper.appendChild(this.parentBottom);
        container.appendChild(wrapper);
      } else {
        container.appendChild(this.parentTop);
        container.appendChild(this.parentBottom);
      }
    },
    shouldQueue() {
      if (!this.queue) return false;
      return this.parentTop.childElementCount > 0 || this.parentBottom.childElementCount > 0;
    },
    dismiss() {
      this.Timer.stop();
      clearTimeout(this.queueTimer);
      this.isActive = false;
      // Timeout for the animation complete before destroying
      this.$el.classList.remove('show');
      this.$el.classList.add('hide');
      setTimeout(() => {
        // eslint-disable-next-line prefer-rest-params
        this.onDismiss.apply(null, arguments);
        this.$destroy();
        removeElement(this.$el);
      }, 150);
    },
    showNotice() {
      if (this.shouldQueue()) {
        // Call recursively if should queue
        this.queueTimer = setTimeout(this.showNotice, 250);
        return;
      }
      this.correctParent.insertAdjacentElement('afterbegin', this.$el);
      this.isActive = true;
      this.Timer = new Timer(this.dismiss, this.duration);
    },
    toggleTimer(newVal) {
      if (!this.pauseOnHover) return;
      if (newVal) this.Timer.pause();
      else this.Timer.resume();
    },
  },
};
</script>

<style lang="scss">
.v-toast {
  background: red;
  display: flex;
  z-index: 1052;

  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 1rem;
  padding: 0;
  height: 0;
  overflow: visible;

  &__item {
    display: block;
    position: relative;
    max-width: 500px;
    backface-visibility: hidden;
    background-clip: padding-box;

    // Individual toast position
    &.v-toast__item--top,
    &.v-toast__item--bottom {
      align-self: center;
    }

    &.v-toast__item--top-right,
    &.v-toast__item--bottom-right {
      align-self: flex-end;
    }

    &.v-toast__item--top-left,
    &.v-toast__item--bottom-left {
      align-self: flex-start;
    }
  }

  // Notice container positions
  &.v-toast--top {
    flex-direction: column;
  }

  &.v-toast--bottom {
    flex-direction: column-reverse;
    top: auto;
  }

  &.v-toast--custom-parent {
    position: absolute;
  }

  @media screen and (max-width: 768px) {
    padding: 0;
    position: fixed !important;
  }
}
</style>
<style scoped>
.show {
  -webkit-animation: fadein 0.25s;
  animation: fadein 0.25s;
}

.hide {
  -webkit-animation: fadeout 0.25s;
  animation: fadeout 0.25s;
}

@-webkit-keyframes fadein {
  from {opacity: 0;}
  to {opacity: 1;}
}

@keyframes fadein {
  from {opacity: 0;}
  to {opacity: 1;}
}

@-webkit-keyframes fadeout {
  from {opacity: 1;}
  to {opacity: 0;}
}

@keyframes fadeout {
  from {opacity: 1;}
  to {opacity: 0;}
}
</style>
